import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Container, 
  Heading, 
  Button, 
  HStack, 
  VStack,
  IconButton,
  Text,
  Badge,
  useToast,
  Select,
  useColorModeValue,
  Tooltip,
  Fade,
  ScaleFade,
  Slide,
  Icon,
  ButtonGroup
} from '@chakra-ui/react';
import { 
  ChevronLeftIcon, 
  ChevronRightIcon, 
  AddIcon,
  CalendarIcon,
  ViewIcon,
  TimeIcon,
  RepeatIcon
} from '@chakra-ui/icons';
import { FaUser, FaUsers } from 'react-icons/fa';
import CalendarView from './CalendarView';
import EventModal from './EventModal';
import EventViewModal from './EventViewModal';
import AgendaFilters from './AgendaFilters';
import ClienteModal from '../Clientes/ClienteModal/ClienteModal';
import { API_BASE_URL } from '../../../../utils/apiConfig';
import './AgendaPage.css';

const AgendaPage = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [view, setView] = useState('month'); // month, week, day
  const [viewMode, setViewMode] = useState('personal'); // personal, team
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [isEventViewModalOpen, setIsEventViewModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [clickedDateTime, setClickedDateTime] = useState(null);
  const [isClienteModalOpen, setIsClienteModalOpen] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [filters, setFilters] = useState({
    userIds: [],
    types: [],
    status: 'all'
  });
  
  const toast = useToast();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const shadowColor = useColorModeValue('gray.200', 'gray.700');
  const hoverShadow = useColorModeValue('lg', 'dark-lg');
  
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const isGestor = userData.permissions?.includes('Gestor');
  const isMaster = userData.permissions?.includes('Master');
  const isDiretoria = userData.permissions?.includes('Diretoria');
  const canViewTeam = isGestor || isMaster || isDiretoria;

  // Fetch events
  const fetchEvents = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Calculate date range based on view
      const start = new Date(selectedDate);
      const end = new Date(selectedDate);
      
      if (view === 'month') {
        start.setDate(1);
        end.setMonth(end.getMonth() + 1, 0);
      } else if (view === 'week') {
        const day = start.getDay();
        start.setDate(start.getDate() - day);
        end.setDate(start.getDate() + 6);
      }
      
      const params = new URLSearchParams({
        start: start.toISOString(),
        end: end.toISOString()
      });
      
      let url = `${API_BASE_URL}/api/events`;
      
      // If viewing team events and has permission
      if (viewMode === 'team' && canViewTeam) {
        url = `${API_BASE_URL}/api/events/team`;
        // If specific users are selected, filter by them
        if (filters.userIds.length > 0) {
          params.append('userIds', filters.userIds.join(','));
        }
      }
      
      const response = await fetch(`${url}?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Erro ao buscar eventos');
      
      const data = await response.json();
      setEvents(data.data || []);
    } catch (error) {
      toast({
        title: 'Erro ao carregar eventos',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [selectedDate, view, viewMode, filters]);

  // Handle calendar navigation
  const handlePrevious = () => {
    const newDate = new Date(selectedDate);
    if (view === 'month') {
      newDate.setMonth(newDate.getMonth() - 1);
    } else if (view === 'week') {
      newDate.setDate(newDate.getDate() - 7);
    } else {
      newDate.setDate(newDate.getDate() - 1);
    }
    setSelectedDate(newDate);
  };

  const handleNext = () => {
    const newDate = new Date(selectedDate);
    if (view === 'month') {
      newDate.setMonth(newDate.getMonth() + 1);
    } else if (view === 'week') {
      newDate.setDate(newDate.getDate() + 7);
    } else {
      newDate.setDate(newDate.getDate() + 1);
    }
    setSelectedDate(newDate);
  };

  const handleToday = () => {
    setSelectedDate(new Date());
  };

  // Handle event operations
  const handleCreateEvent = () => {
    setSelectedEvent(null);
    setClickedDateTime(null);
    setIsEventModalOpen(true);
  };

  const handleTimeSlotClick = (date, time) => {
    setClickedDateTime({ date, time });
    setSelectedEvent(null);
    setIsEventModalOpen(true);
  };

  const handleEventClick = (event) => {
    setSelectedEvent(event);
    setClickedDateTime(null);
    setIsEventViewModalOpen(true);
  };

  const handleEditClick = () => {
    setIsEventViewModalOpen(false);
    setIsEventModalOpen(true);
  };

  const handleEventModalClose = () => {
    setIsEventModalOpen(false);
    setSelectedEvent(null);
    setClickedDateTime(null);
  };

  const handleEventViewModalClose = () => {
    setIsEventViewModalOpen(false);
    setSelectedEvent(null);
    setClickedDateTime(null);
  };

  const handleEventSave = async (eventData) => {
    try {
      const token = localStorage.getItem('token');
      const url = selectedEvent 
        ? `${API_BASE_URL}/api/events/${selectedEvent._id}`
        : `${API_BASE_URL}/api/events`;
      
      const response = await fetch(url, {
        method: selectedEvent ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(eventData)
      });
      
      if (!response.ok) throw new Error('Erro ao salvar evento');
      
      toast({
        title: selectedEvent ? 'Evento atualizado' : 'Evento criado',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      setIsEventModalOpen(false);
      fetchEvents();
    } catch (error) {
      toast({
        title: 'Erro ao salvar evento',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleEventDelete = async (eventId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/events/${eventId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Erro ao excluir evento');
      
      toast({
        title: 'Evento excluído',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      setIsEventModalOpen(false);
      fetchEvents();
    } catch (error) {
      toast({
        title: 'Erro ao excluir evento',
        description: error.message,
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleClientClick = (cliente) => {
    setSelectedCliente(cliente);
    setIsClienteModalOpen(true);
  };

  const handleClienteModalClose = () => {
    setIsClienteModalOpen(false);
    setSelectedCliente(null);
  };

  // Format current date display
  const formatDateDisplay = () => {
    const options = { month: 'long', year: 'numeric' };
    if (view === 'week') {
      const start = new Date(selectedDate);
      const end = new Date(selectedDate);
      const day = start.getDay();
      start.setDate(start.getDate() - day);
      end.setDate(start.getDate() + 6);
      return `${start.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString('pt-BR', { day: 'numeric', month: 'short', year: 'numeric' })}`;
    } else if (view === 'day') {
      return selectedDate.toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    }
    return selectedDate.toLocaleDateString('pt-BR', options);
  };

  return (
    <Container maxW="100%" p={4}>
      <Fade in={true}>
        <VStack spacing={6} align="stretch">
          {/* Header */}
          <ScaleFade in={true} initialScale={0.95}>
            <Box 
              bg={bgColor} 
              p={6} 
              borderRadius="lg" 
              borderWidth="1px" 
              borderColor={borderColor}
              boxShadow="sm"
              transition="all 0.3s"
              _hover={{ boxShadow: hoverShadow }}
            >
              <HStack justify="space-between" mb={4}>
                <HStack spacing={4}>
                  <Icon 
                    as={CalendarIcon} 
                    boxSize={8} 
                    color="green.500"
                    transition="transform 0.3s"
                    _hover={{ transform: 'rotate(-5deg) scale(1.1)' }}
                  />
                  <Heading size="lg" bgGradient="linear(to-r, green.500, green.300)" bgClip="text">
                    Agenda
                  </Heading>
                  {canViewTeam && (
                    <ButtonGroup size="sm" isAttached variant="outline">
                      <Button
                        leftIcon={<Icon as={FaUser} />}
                        isActive={viewMode === 'personal'}
                        onClick={() => {
                          setViewMode('personal');
                          // Clear user filters when switching to personal view
                          setFilters(prev => ({ ...prev, userIds: [] }));
                        }}
                        colorScheme={viewMode === 'personal' ? 'green' : 'gray'}
                        _active={{ bg: 'green.50', borderColor: 'green.500' }}
                      >
                        Minha Agenda
                      </Button>
                      <Button
                        leftIcon={<Icon as={FaUsers} />}
                        isActive={viewMode === 'team'}
                        onClick={() => setViewMode('team')}
                        colorScheme={viewMode === 'team' ? 'purple' : 'gray'}
                        _active={{ bg: 'purple.50', borderColor: 'purple.500' }}
                      >
                        Equipe
                      </Button>
                    </ButtonGroup>
                  )}
                </HStack>
                <Tooltip label="Criar novo evento" placement="left">
                  <Button
                    leftIcon={<AddIcon />}
                    colorScheme="green"
                    onClick={handleCreateEvent}
                    size="md"
                    _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
                    transition="all 0.2s"
                  >
                    Novo Evento
                  </Button>
                </Tooltip>
              </HStack>

              {/* Controls */}
              <HStack justify="space-between" spacing={4} flexWrap="wrap">
                {/* Navigation */}
                <HStack spacing={3}>
                  <Tooltip label="Mês anterior" placement="top">
                    <IconButton
                      icon={<ChevronLeftIcon boxSize={5} />}
                      onClick={handlePrevious}
                      aria-label="Anterior"
                      variant="outline"
                      colorScheme="gray"
                      _hover={{ transform: 'translateX(-2px)' }}
                      transition="all 0.2s"
                    />
                  </Tooltip>
                  
                  <Tooltip label="Ir para hoje" placement="top">
                    <Button 
                      variant="outline" 
                      onClick={handleToday}
                      leftIcon={<TimeIcon />}
                      colorScheme="green"
                      _hover={{ transform: 'scale(1.05)' }}
                      transition="all 0.2s"
                    >
                      Hoje
                    </Button>
                  </Tooltip>
                  
                  <Tooltip label="Próximo mês" placement="top">
                    <IconButton
                      icon={<ChevronRightIcon boxSize={5} />}
                      onClick={handleNext}
                      aria-label="Próximo"
                      variant="outline"
                      colorScheme="gray"
                      _hover={{ transform: 'translateX(2px)' }}
                      transition="all 0.2s"
                    />
                  </Tooltip>
                  
                  <Box 
                    px={4} 
                    py={2} 
                    bg="green.50" 
                    borderRadius="md"
                    transition="all 0.3s"
                    _hover={{ bg: 'green.100' }}
                  >
                    <Text fontSize="lg" fontWeight="bold" color="green.700">
                      {formatDateDisplay()}
                    </Text>
                  </Box>
                </HStack>

                {/* View Selector */}
                <HStack spacing={3}>
                  <Icon as={ViewIcon} color="gray.500" />
                  <Select
                    value={view}
                    onChange={(e) => setView(e.target.value)}
                    w="150px"
                    borderRadius="md"
                    _hover={{ borderColor: "gray.400" }}
                    _focus={{ borderColor: "green.500", boxShadow: "0 0 0 1px green.500" }}
                  >
                    <option value="month">Mês</option>
                    <option value="week">Semana</option>
                    <option value="day">Dia</option>
                  </Select>
                  
                  <Tooltip label="Atualizar eventos" placement="top">
                    <IconButton
                      icon={<RepeatIcon />}
                      onClick={fetchEvents}
                      aria-label="Atualizar"
                      variant="ghost"
                      colorScheme="gray"
                      isLoading={loading}
                      _hover={{ transform: 'rotate(180deg)' }}
                      transition="all 0.3s"
                    />
                  </Tooltip>
                </HStack>
              </HStack>
              
              {/* View Mode Indicator */}
              {viewMode === 'team' && (
                <Box mt={3} p={2} bg="purple.50" borderRadius="md">
                  <HStack spacing={2}>
                    <Icon as={FaUsers} color="purple.600" />
                    <Text fontSize="sm" color="purple.700" fontWeight="medium">
                      Visualizando eventos da equipe
                      {filters.userIds.length > 0 && ` (${filters.userIds.length} usuário${filters.userIds.length > 1 ? 's' : ''} selecionado${filters.userIds.length > 1 ? 's' : ''})`}
                    </Text>
                  </HStack>
                </Box>
              )}
            </Box>
          </ScaleFade>

          {/* Filters - Only show in team view */}
          {canViewTeam && viewMode === 'team' && (
            <ScaleFade in={viewMode === 'team'} initialScale={0.95}>
              <Box mb={4}>
                <AgendaFilters
                  filters={filters}
                  onFiltersChange={setFilters}
                />
              </Box>
            </ScaleFade>
          )}

          {/* Calendar */}
          <ScaleFade in={!loading} initialScale={0.98}>
            <Box 
              bg={bgColor} 
              p={6} 
              borderRadius="lg" 
              borderWidth="1px" 
              borderColor={borderColor}
              boxShadow="md"
              transition="all 0.3s"
              _hover={{ boxShadow: hoverShadow }}
              position="relative"
              overflow="hidden"
            >
              {/* Decorative gradient overlay */}
              <Box
                position="absolute"
                top={0}
                left={0}
                right={0}
                h="4px"
                bgGradient="linear(to-r, green.300, green.500, green.300)"
                animation="shimmer 3s ease-in-out infinite"
              />
              
              <CalendarView
                events={events}
                view={view}
                selectedDate={selectedDate}
                onTimeSlotClick={handleTimeSlotClick}
                onEventClick={handleEventClick}
                loading={loading}
              />
            </Box>
          </ScaleFade>
        </VStack>
      </Fade>

      {/* Event Modal */}
      {/* Event View Modal */}
      <EventViewModal
        isOpen={isEventViewModalOpen}
        onClose={handleEventViewModalClose}
        event={selectedEvent}
        onEdit={handleEditClick}
        onClientClick={handleClientClick}
      />

      {/* Event Modal */}
      <EventModal
        isOpen={isEventModalOpen}
        onClose={handleEventModalClose}
        event={selectedEvent}
        clickedDateTime={clickedDateTime}
        onSave={handleEventSave}
        onDelete={handleEventDelete}
      />

      {/* Cliente Modal */}
      {selectedCliente && (
        <ClienteModal
          isOpen={isClienteModalOpen}
          onClose={handleClienteModalClose}
          cliente={selectedCliente}
        />
      )}
    </Container>
  );
};

export default AgendaPage;